import classNames from "classnames"
import Cookies from "js-cookie"
import ApplicationComponent from "legacy/ApplicationComponent"
import LegacyButton from "legacy/LegacyButton"
import PropTypes from "prop-types"
import React from "react"

/**
 * SVGCanvas takes in svg objects and view box props to generate an svg canvas for those objects to be rendered within a specific context. The view box is one of the most important aspects of this component. The viewbox sets the scale for the number units that objects are being built off within the canvas.
 * @param {boolean} editing - whether the controller is in editing state or not.
 * @param {string} id - unique identifier for the component that allows us to save cookies for zoom levels based on what this is rendering
 * @param {method} setFormState - method that takes in an x and y param and sets formData state based on those values.
 * @param {boolean} setZoomFromCookie - indicates whether the zoom level should be read from and save to cookies
 * @param {string} viewBoxWidth - a string of a number that represents how many "units" wide the canvas is.
 * @param {string} viewBoxHeight - a string of a number that represents how many "units" tall the canvas is.
 * @param {string} viewBoxX - a string of a number that sets the context for where the 0 x coordinate is for the children of the canvas component.
 * @param {string} viewBoxY - a string of a number that sets the context for where the 0 y coordinate is for the children of the canvas component.
 */
class SvgCanvas extends ApplicationComponent {
  constructor(props) {
    super(props)
    this.state = {
      viewBoxX: 0,
      viewBoxY: 0,
      draggingMap: false,
      draggingRect: false,
      transformString: "",
      scale: 1,
      translateX: 0,
      translateY: 0,
      minTranslateX: 0,
      minTranslateY: 0,
      maxTranslateX: 0,
      maxTranslateY: 0,
    }
    this.svg = null
    this.group = null
    this.annotationRect = null
    this.bulkActionRect = null

    this.zoom = this.zoom.bind(this)
    this.startDrag = this.startDrag.bind(this)
    this.drag = this.drag.bind(this)
    this.endDrag = this.endDrag.bind(this)
    this.setZoomFromCookie = this.setZoomFromCookie.bind(this)
    this.setSvgRef = this.setSvgRef.bind(this)
    this.setGroupRef = this.setGroupRef.bind(this)
    this.doNothing = this.doNothing.bind(this)

    // Initialize from cookie in constructor for sync operation
    if (props.setZoomFromCookie) {
      this.initZoomFromCookie() // Use the version safe for constructor
    }
  }

  setSvgRef(element) {
    this.svg = element
  }

  setGroupRef(element) {
    this.group = element
  }

  componentDidMount() {
    this.svg.addEventListener("dragstart", this.doNothing) // Required for Firefox
    this.addEventsListener(
      this.svg,
      ["mousedown", "touchstart"],
      this.startDrag
    )
    this.addEventsListener(window, ["mousemove", "touchmove"], this.drag)
    this.addEventsListener(window, ["mouseup", "touchend"], this.endDrag)

    // If we initialized from cookies in constructor, we need to call setGlobalState now
    if (
      this.props.setZoomFromCookie &&
      this.props.setGlobalState &&
      this.state.transformString
    ) {
      this.props.setGlobalState({
        transformString: this.state.transformString,
      })
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.editing !== prevProps.editing && this.props.editing) {
      if (this.props.performingBulkAction) {
        this.bulkActionRect = document.getElementById("bulk-action-rect")
        this.bulkActionRect.addEventListener("mousedown", this.startDrag)
      } else {
        this.annotationRect = document.getElementById("annotation-rect")
        this.annotationRect.addEventListener("mousedown", this.startDrag)
      }
    }
  }

  componentWillUnmount() {
    if (this.props.annotationRect) {
      this.annotationRect.removeEventListener("mousedown", this.startDrag)
    }
    this.svg.removeEventListener("dragstart", this.doNothing) // Required for Firefox
    this.removeEventsListener(
      this.svg,
      ["mousedown", "touchstart"],
      this.startDrag
    )
    this.removeEventsListener(window, ["mousemove", "touchmove"], this.drag)
    this.removeEventsListener(window, ["mouseup", "touchend"], this.endDrag)
  }

  isLeftClick(event) {
    return event.button === 0
  }

  setZoomFromCookie() {
    // This version is used after component mount
    if (Cookies.get(`${this.props.id}:zoomContext`)) {
      const zoomState = JSON.parse(Cookies.get(`${this.props.id}:zoomContext`))
      this.setState(zoomState)
      this.props.setGlobalState &&
        this.props.setGlobalState({
          transformString: zoomState.transformString,
        })
    }
  }

  // This version is used in constructor, before component mount
  initZoomFromCookie() {
    if (Cookies.get(`${this.props.id}:zoomContext`)) {
      const zoomState = JSON.parse(Cookies.get(`${this.props.id}:zoomContext`))
      // Directly update this.state instead of using setState
      this.state = { ...this.state, ...zoomState }
      // Cannot call setGlobalState here as component is not mounted
    }
    return this.state
  }

  doNothing() {
    return false
  }

  /**
   * getMousePosition finds where on the svg the window event has occured.
   * @param  {object} event - window event object
   * @return {object} - hash with x and y coordinates of where in the svg the mouse event occurred
   */
  getMousePosition(event) {
    const CTM = this.svg.getScreenCTM()
    if (event.touches) {
      event = event.touches[0]
    }
    return {
      x: (event.clientX - CTM.e) / (CTM.a * this.state.scale),
      y: (event.clientY - CTM.f) / (CTM.d * this.state.scale),
    }
  }

  /**
   * ensures the click was a left click then captures the current mouse position and sets it to state.
   * @param  {object} event - window event object
   */
  startDrag(event) {
    if (
      (!this.state.draggingMap && (this.isLeftClick(event) || event.touches)) ||
      (!this.state.draggingRect && this.isLeftClick(event))
    ) {
      event.stopPropagation()
      if (event.touches === undefined) {
        event.preventDefault()
      } else {
        document.body.classList.add("overflow-hidden")
      }
      const mouseOffset = this.getMousePosition(event)
      if (this.annotationRect && this.annotationRect.contains(event.target)) {
        mouseOffset.x -= parseInt(this.annotationRect.getAttribute("x"))
        mouseOffset.y -= parseInt(this.annotationRect.getAttribute("y"))
        this.setState({
          draggingRect: true,
          mouseOffset,
        })
      } else if (
        this.bulkActionRect &&
        this.bulkActionRect.contains(event.target)
      ) {
        mouseOffset.x -= parseInt(this.bulkActionRect.getAttribute("x"))
        mouseOffset.y -= parseInt(this.bulkActionRect.getAttribute("y"))
        this.setState({
          draggingRect: true,
          mouseOffset,
        })
      } else {
        mouseOffset.x -= this.state.translateX
        mouseOffset.y -= this.state.translateY
        this.setState({
          draggingMap: true,
          mouseOffset,
        })
      }
    }
  }

  /**
   * drag translates mouse movements while hold-clicking into translations applied to the svg content window.
   * @param  {object} event - window event object
   */
  drag(event) {
    if (this.state.draggingMap || this.state.draggingRect) {
      event.stopPropagation()
      if (event.touches === undefined) {
        event.preventDefault()
      }
      const mouseOffset = this.getMousePosition(event)
      const translateX = mouseOffset.x - this.state.mouseOffset.x
      const translateY = mouseOffset.y - this.state.mouseOffset.y
      if (this.state.draggingMap) {
        this.positionWindowWithinBoundaries(translateX, translateY)
      } else if (this.state.draggingRect) {
        if (this.annotationRect) {
          this.annotationRect.setAttribute("x", translateX)
          this.annotationRect.setAttribute("y", translateY)
          this.props.setFormState(
            parseInt(translateX) + 8,
            parseInt(translateY) + 8
          )
        } else if (this.bulkActionRect) {
          this.bulkActionRect.setAttribute("x", translateX)
          this.bulkActionRect.setAttribute("y", translateY)
          this.props.setFormState(parseInt(translateX), parseInt(translateY))
        }
      }
    }
  }

  calculateWindowBoundaries(scale) {
    const maxX = (this.props.viewBoxWidth * (1 - 1 / scale)) / 2
    const maxY = (this.props.viewBoxHeight * (1 - 1 / scale)) / 2
    this.setState(
      {
        scale,
        minTranslateX: maxX * -1,
        maxTranslateX: maxX,
        minTranslateY: maxY * -1,
        maxTranslateY: maxY,
      },
      this.positionWindowWithinBoundaries
    )
  }

  /**
   * Handles mouseup event to stop dragging
   * @param  {object} event - window event object
   */
  endDrag(event) {
    if (event.touches !== undefined) {
      document.body.classList.remove("overflow-hidden")
    }
    this.setState({ draggingMap: false, draggingRect: false })
  }

  /**
   * Accepts translation and scale data and applies transforms that are valid with the
   * included boundaries
   * @param  {float} scale - the current zoom scale applied to the content window
   * @param  {float} x - the desired coordinate to translate the content window along the x axis
   * @param  {float} y - the desired coordinate to translate the content window along the y axis
   * @param  {float} minX - the minimum x value for translation
   * @param  {float} minY - the minimum y value for translation
   */
  positionWindowWithinBoundaries(
    x = this.state.translateX,
    y = this.state.translateY
  ) {
    if (x < this.state.minTranslateX) {
      x = this.state.minTranslateX
    } else if (x > this.state.maxTranslateX) {
      x = this.state.maxTranslateX
    }
    if (y < this.state.minTranslateY) {
      y = this.state.minTranslateY
    } else if (y > this.state.maxTranslateY) {
      y = this.state.maxTranslateY
    }

    const zoomState = {
      translateX: x,
      translateY: y,
      transformString: this.getTransformString(this.state.scale, x, y),
    }

    if (this.props.setZoomFromCookie) {
      Cookies.set(`${this.props.id}:zoomContext`, JSON.stringify(zoomState))
    }

    this.setState(zoomState)
    this.props.setGlobalState &&
      this.props.setGlobalState({ transformString: zoomState.transformString })
  }

  /**
   * takes in a direction param and increments or decrements the scale associated with the viewbox of the Canvas component depending on the param.
   * @param {string} direction
   */
  zoom(direction) {
    let scale = this.state.scale
    if (direction === "in") {
      scale += 0.2
    }
    if (direction === "out" && this.state.scale > 1) {
      scale -= 0.2
    }

    this.calculateWindowBoundaries(scale)
  }

  /**
   * @param {number} scale - a number equal to the current scale of the images and shapes within the canvas component.
   * @param {number} x - where along the x axis the object being transformed will end up.
   * @param {number} y - where along the y axis the object being transformed will end up
   * @returns {string} - this string is generated based on the scale and panning of the images and objects within the Canvas component is applied directly to the transform attribute of those objects.
   */
  getTransformString(scale, x, y) {
    return `scale(${scale}, ${scale}) translate(${x}, ${y})`
  }

  addEventsListener(object, events, method) {
    events.forEach((event) => {
      object.addEventListener(event, method)
    })
  }

  removeEventsListener(object, events, method) {
    events.forEach((event) => {
      object.removeEventListener(event, method)
    })
  }

  renderZoom() {
    return (
      <div className="card sticky bottom-4 z-50 -mt-20 mb-4 ml-4 flex w-min justify-center p-3">
        <div className="bg-gray flex flex-row rounded border border-gray-300 text-gray-500">
          <LegacyButton
            className="rounded-r-none focus:z-10"
            type="tertiary"
            onClick={() => this.zoom("out")}
            icon="fa-window-minimize"
            iconStyle="solid"
            iconOnly={true}
          />
          <LegacyButton
            className="rounded-l-none focus:z-10"
            type="tertiary"
            onClick={() => this.zoom("in")}
            icon="fa-plus"
            iconStyle="solid"
            iconOnly={true}
          />
        </div>
      </div>
    )
  }

  render() {
    return (
      <div className="relative">
        <div
          className={classNames("card p-0", {
            "rounded-t-none": this.props.hasHeader,
            "border-t-0": this.props.hasHeader,
          })}
        >
          <svg
            className={classNames({
              "cursor-move": this.state.draggingMap || this.state.draggingRect,
            })}
            height="100%"
            width="100%"
            viewBox={`${this.props.viewBoxX} ${this.props.viewBoxY} ${this.props.viewBoxWidth} ${this.props.viewBoxHeight}`}
            ref={this.setSvgRef}
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
          >
            <g
              ref={this.setGroupRef}
              style={{ transformOrigin: "50% 50%" }}
              transform={this.state.transformString}
              className={classNames({
                "transition-transform": !this.state.draggingMap,
              })}
            >
              {/* Make sure this image stays full width and height to always fill the parent */}
              {/* Standard SVG image element - this is the approach used in the original code */}
              {this.props.backgroundImage ? (
                <image
                  className="h-full w-full"
                  x="0"
                  y="0"
                  href={this.props.backgroundImage}
                  preserveAspectRatio="none"
                />
              ) : null}
              {this.props.children}
            </g>
          </svg>
        </div>
        {this.renderZoom()}
        <style>
          {/* Used to lock screen when touch panning */}
          {"body.overflow-hidden { overflow: hidden }"}
        </style>
      </div>
    )
  }
}

SvgCanvas.propTypes = {
  editing: PropTypes.bool,
  id: PropTypes.string.isRequired,
  setFormState: PropTypes.func,
  setZoomFromCookie: PropTypes.bool,
  viewBoxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  viewBoxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  viewBoxX: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  viewBoxY: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  backgroundImage: PropTypes.string,
  hasHeader: PropTypes.bool,
  performingBulkAction: PropTypes.bool,
  setGlobalState: PropTypes.func,
  children: PropTypes.node,
  annotationRect: PropTypes.bool,
}

SvgCanvas.defaultProps = {
  viewBoxX: 0,
  viewBoxY: 0,
  setZoomFromCookie: false,
}

export default SvgCanvas
